/* ==========================================================================
   AJ Giveaway Showcase
   Powers: sections/giveaway-vehicles.liquid
           sections/giveaway-winners.liquid

   - Scroll reveal (fail-safe: cards are visible unless JS opts them in)
   - "Show all" expansion
   - Shared lightbox built on <dialog>.showModal()

   Why <dialog>: it renders in the browser's top layer, so it cannot be
   buried by any of the store's app overlays no matter their z-index, and we
   inherit Esc-to-close, focus trapping and background inerting natively.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__gvShowcaseLoaded) return;
  window.__gvShowcaseLoaded = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ----------------------------------------------------------------------
     Scroll reveal
     ---------------------------------------------------------------------- */

  function initReveal(root) {
    if (REDUCED.matches || !('IntersectionObserver' in window)) return;

    var grids = (root || document).querySelectorAll('[data-gv-reveal]');

    Array.prototype.forEach.call(grids, function (grid) {
      if (grid.__gvRevealObserver) return;

      var section = grid.closest('.gv');
      if (!section) return;
      section.classList.add('gv--reveal');

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var item = entry.target;
            var row = Array.prototype.indexOf.call(item.parentNode.children, item);
            item.style.setProperty('--gv-delay', Math.min(row, 5) * 70 + 'ms');
            item.classList.add('gv--in');
            observer.unobserve(item);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );

      grid.__gvRevealObserver = observer;
      grid.__gvRevealObserve = function (nodes) {
        Array.prototype.forEach.call(nodes, function (n) {
          if (!n.classList.contains('gv--in')) observer.observe(n);
        });
      };
      grid.__gvRevealObserve(grid.querySelectorAll('.gv__item:not([hidden])'));
    });
  }

  /* ----------------------------------------------------------------------
     Show all
     ---------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-gv-more]');
    if (!button) return;

    var section = button.closest('.gv');
    if (!section) return;

    var grid = section.querySelector('[data-gv-grid]');
    if (!grid) return;

    var revealed = grid.querySelectorAll('.gv__item[hidden]');
    Array.prototype.forEach.call(revealed, function (item) {
      item.removeAttribute('hidden');
    });

    if (grid.__gvRevealObserve) grid.__gvRevealObserve(revealed);

    var wrap = button.closest('.gv__more-wrap');
    if (wrap) wrap.remove();
  });

  /* ----------------------------------------------------------------------
     Lightbox
     ---------------------------------------------------------------------- */

  var ICON_CLOSE =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  var ICON_PREV =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
  var ICON_NEXT =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>';

  var lb = null;

  function buildLightbox() {
    if (lb) return lb;

    var dialog = document.createElement('dialog');
    dialog.className = 'gv-lb';
    dialog.setAttribute('aria-label', 'Image viewer');
    dialog.innerHTML =
      '<div class="gv-lb__stage" data-gv-lb-stage>' +
      '<figure class="gv-lb__panel" data-gv-lb-panel>' +
      '<img class="gv-lb__img" data-gv-lb-img alt="">' +
      '<figcaption class="gv-lb__cap" data-gv-lb-cap></figcaption>' +
      '</figure>' +
      '</div>' +
      '<button type="button" class="gv-lb__btn gv-lb__close" data-gv-lb-close aria-label="Close">' +
      ICON_CLOSE +
      '</button>' +
      '<button type="button" class="gv-lb__btn gv-lb__prev" data-gv-lb-prev aria-label="Previous image">' +
      ICON_PREV +
      '</button>' +
      '<button type="button" class="gv-lb__btn gv-lb__next" data-gv-lb-next aria-label="Next image">' +
      ICON_NEXT +
      '</button>' +
      '<p class="gv-lb__counter" data-gv-lb-counter aria-live="polite"></p>';

    document.body.appendChild(dialog);

    lb = {
      dialog: dialog,
      stage: dialog.querySelector('[data-gv-lb-stage]'),
      panel: dialog.querySelector('[data-gv-lb-panel]'),
      img: dialog.querySelector('[data-gv-lb-img]'),
      cap: dialog.querySelector('[data-gv-lb-cap]'),
      close: dialog.querySelector('[data-gv-lb-close]'),
      prev: dialog.querySelector('[data-gv-lb-prev]'),
      next: dialog.querySelector('[data-gv-lb-next]'),
      counter: dialog.querySelector('[data-gv-lb-counter]'),
      items: [],
      index: 0,
      opener: null,
      scrollY: 0
    };

    lb.close.addEventListener('click', closeLightbox);
    lb.prev.addEventListener('click', function () { step(-1); });
    lb.next.addEventListener('click', function () { step(1); });

    // Click anywhere that is not the panel closes.
    lb.stage.addEventListener('click', function (event) {
      if (!event.target.closest('[data-gv-lb-panel]')) closeLightbox();
    });

    // Native Esc fires `cancel`; route it through our own teardown.
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeLightbox();
    });

    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
    });

    bindSwipe();

    return lb;
  }

  function bindSwipe() {
    var startX = 0;
    var startY = 0;
    var dx = 0;
    var dy = 0;
    var tracking = false;

    lb.panel.addEventListener(
      'touchstart',
      function (event) {
        if (event.touches.length !== 1) return;
        tracking = true;
        dx = 0;
        dy = 0;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        lb.dialog.classList.add('gv-lb--dragging');
      },
      { passive: true }
    );

    lb.panel.addEventListener(
      'touchmove',
      function (event) {
        if (!tracking) return;
        dx = event.touches[0].clientX - startX;
        dy = event.touches[0].clientY - startY;
        if (dy > 0 && Math.abs(dy) > Math.abs(dx)) {
          lb.panel.style.transform = 'translateY(' + dy * 0.5 + 'px)';
          lb.panel.style.opacity = String(Math.max(0.35, 1 - dy / 420));
        }
      },
      { passive: true }
    );

    lb.panel.addEventListener('touchend', function () {
      if (!tracking) return;
      tracking = false;
      lb.dialog.classList.remove('gv-lb--dragging');
      lb.panel.style.transform = '';
      lb.panel.style.opacity = '';

      if (dy > 90 && Math.abs(dy) > Math.abs(dx)) closeLightbox();
      else if (dx > 60 && Math.abs(dx) > Math.abs(dy)) step(-1);
      else if (dx < -60 && Math.abs(dx) > Math.abs(dy)) step(1);
    });
  }

  function collectGroup(group) {
    var all = document.querySelectorAll('[data-gv-lb="' + group + '"]');
    return Array.prototype.filter.call(all, function (node) {
      var item = node.closest('.gv__item');
      return !(item && item.hasAttribute('hidden'));
    });
  }

  function render() {
    var node = lb.items[lb.index];
    if (!node) return;

    var width = node.getAttribute('data-gv-w');
    var height = node.getAttribute('data-gv-h');

    lb.img.removeAttribute('width');
    lb.img.removeAttribute('height');
    if (width) lb.img.setAttribute('width', width);
    if (height) lb.img.setAttribute('height', height);

    lb.img.src = node.getAttribute('data-gv-src') || '';
    lb.img.alt = node.getAttribute('data-gv-alt') || '';

    // Match the panel to the palette of the section that opened it.
    lb.dialog.classList.toggle('gv-lb--light', node.getAttribute('data-gv-theme') === 'light');

    var name = node.getAttribute('data-gv-name') || '';
    var meta = node.getAttribute('data-gv-meta') || '';
    var caption = '';
    if (name) caption += '<span class="gv-lb__cap-name">' + name + '</span>';
    if (meta) caption += '<span class="gv-lb__cap-meta">' + meta + '</span>';
    lb.cap.innerHTML = caption;

    var multi = lb.items.length > 1;
    lb.prev.hidden = !multi;
    lb.next.hidden = !multi;
    lb.counter.hidden = !multi;
    lb.dialog.classList.toggle('gv-lb--multi', multi);
    if (multi) lb.counter.textContent = lb.index + 1 + ' / ' + lb.items.length;

    preload(lb.index + 1);
    preload(lb.index - 1);
  }

  function preload(index) {
    if (lb.items.length < 2) return;
    var wrapped = (index + lb.items.length) % lb.items.length;
    var node = lb.items[wrapped];
    if (!node) return;
    var src = node.getAttribute('data-gv-src');
    if (!src) return;
    var img = new Image();
    img.src = src;
  }

  function step(direction) {
    if (lb.items.length < 2) return;
    lb.index = (lb.index + direction + lb.items.length) % lb.items.length;
    render();
  }

  function lockScroll() {
    var doc = document.documentElement;
    var gap = window.innerWidth - doc.clientWidth;
    lb.prevOverflow = doc.style.overflow;
    lb.prevPadding = doc.style.paddingRight;
    doc.style.overflow = 'hidden';
    if (gap > 0) doc.style.paddingRight = gap + 'px';
  }

  function unlockScroll() {
    var doc = document.documentElement;
    doc.style.overflow = lb.prevOverflow || '';
    doc.style.paddingRight = lb.prevPadding || '';
  }

  function openLightbox(trigger) {
    buildLightbox();

    var group = trigger.getAttribute('data-gv-lb');
    lb.items = collectGroup(group);
    lb.index = Math.max(0, lb.items.indexOf(trigger));
    lb.opener = trigger;

    render();
    lockScroll();

    if (typeof lb.dialog.showModal === 'function') {
      lb.dialog.showModal();
    } else {
      // Very old browser: no top layer, so fake it.
      lb.dialog.setAttribute('open', '');
      lb.dialog.style.position = 'fixed';
      lb.dialog.style.inset = '0';
      lb.dialog.style.zIndex = '2147483000';
      lb.dialog.style.background = 'rgba(4,6,10,0.92)';
      document.addEventListener('keydown', escFallback);
    }

    lb.close.focus({ preventScroll: true });
  }

  function escFallback(event) {
    if (event.key === 'Escape') closeLightbox();
  }

  function closeLightbox() {
    if (!lb || !lb.dialog.hasAttribute('open')) return;

    if (typeof lb.dialog.close === 'function' && typeof lb.dialog.showModal === 'function') {
      lb.dialog.close();
    } else {
      lb.dialog.removeAttribute('open');
      lb.dialog.style.cssText = '';
      document.removeEventListener('keydown', escFallback);
    }

    unlockScroll();
    lb.img.removeAttribute('src');

    if (lb.opener && document.contains(lb.opener)) {
      lb.opener.focus({ preventScroll: true });
    }
    lb.opener = null;
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-gv-lb]');
    if (!trigger) return;
    event.preventDefault();
    openLightbox(trigger);
  });

  /* ----------------------------------------------------------------------
     Boot + theme editor
     ---------------------------------------------------------------------- */

  function boot() {
    initReveal(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initReveal(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    closeLightbox();
  });
})();
